import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import CookieBanner from '../../components/feature/CookieBanner';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import OptimizedImage from '../../components/base/OptimizedImage';

const HomePage = () => {
  useEffect(() => {
    document.title = 'Instalación de Pladur en Sabadell | PLADUREX - Tabiques y Techos';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Instalación de pladur en Sabadell profesional. Tabiques, trasdosados, falsos techos y aislamiento acústico. Obra limpia y rápida. Presupuesto sin compromiso ☎ 629 501 941');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Instalación de pladur en Sabadell profesional. Tabiques, trasdosados, falsos techos y aislamiento acústico. Obra limpia y rápida. Presupuesto sin compromiso ☎ 629 501 941';
      document.head.appendChild(meta);
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'instalación de pladur en Sabadell, tabiques pladur Sabadell, falsos techos Sabadell, aislamiento acústico, trasdosados');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'instalación de pladur en Sabadell, tabiques pladur Sabadell, falsos techos Sabadell, aislamiento acústico, trasdosados';
      document.head.appendChild(meta);
    }

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Instalación de Pladur en Sabadell | PLADUREX');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:title');
      meta.content = 'Instalación de Pladur en Sabadell | PLADUREX';
      document.head.appendChild(meta);
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Especialistas en instalación de pladur en Sabadell. Tabiques, techos, aislamientos. Obra limpia y profesional. Presupuesto gratis.');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:description');
      meta.content = 'Especialistas en instalación de pladur en Sabadell. Tabiques, techos, aislamientos. Obra limpia y profesional. Presupuesto gratis.';
      document.head.appendChild(meta);
    }

    const jsonLd = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "LocalBusiness",
          "@id": "https://sabadell.pladures.es/#business",
          "name": "PLADUREX",
          "image": "https://public.readdy.ai/ai/img_res/06424b75-440f-4c86-baff-26ebcba943a6.png",
          "telephone": "+34629501941",
          "url": "https://sabadell.pladures.es",
          "areaServed": [
            {
              "@type": "City",
              "name": "Sabadell"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Vallès Occidental"
            },
            {
              "@type": "City",
              "name": "Barcelona"
            }
          ],
          "priceRange": "€€",
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "08:00",
            "closes": "19:00"
          }
        },
        {
          "@type": "Service",
          "@id": "https://sabadell.pladures.es/#service",
          "serviceType": "Instalación de Pladur",
          "provider": {
            "@id": "https://sabadell.pladures.es/#business"
          },
          "areaServed": {
            "@type": "City",
            "name": "Sabadell"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Servicios de Pladur",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Tabiques de pladur"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Trasdosados"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Falsos techos"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Aislamiento acústico"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Aislamiento térmico"
                }
              }
            ]
          }
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    {
      icon: 'ri-layout-grid-line',
      title: 'Tabiques de Pladur',
      description: 'Divisiones interiores rápidas y limpias para redistribuir espacios en viviendas, oficinas y locales comerciales.',
      link: '/servicios#tabiques'
    },
    {
      icon: 'ri-building-line',
      title: 'Trasdosados',
      description: 'Directos y autoportantes con aislamiento térmico y acústico para mejorar el confort de tu hogar u oficina.',
      link: '/servicios#trasdosados'
    },
    {
      icon: 'ri-stack-line',
      title: 'Falsos Techos',
      description: 'Techos continuos y registrables que ocultan instalaciones y mejoran la estética de cualquier espacio.',
      link: '/servicios#techos'
    },
    {
      icon: 'ri-volume-mute-line',
      title: 'Aislamiento Acústico',
      description: 'Soluciones de insonorización para reducir el ruido entre estancias y mejorar la privacidad acústica.',
      link: '/servicios#acustico'
    },
    {
      icon: 'ri-temp-cold-line',
      title: 'Aislamiento Térmico',
      description: 'Mejora la eficiencia energética de tu vivienda con sistemas de aislamiento térmico de alta calidad.',
      link: '/servicios#termico'
    },
    {
      icon: 'ri-drop-line',
      title: 'Pladur Antihumedad',
      description: 'Placas especiales para baños, cocinas y lavaderos que resisten la humedad y evitan problemas futuros.',
      link: '/servicios#antihumedad'
    }
  ];

  const benefits = [
    {
      icon: 'ri-time-line',
      title: 'Obra Rápida',
      description: 'Instalación ágil sin largos plazos de espera'
    },
    {
      icon: 'ri-brush-line',
      title: 'Acabados Limpios',
      description: 'Superficies lisas listas para pintar'
    },
    {
      icon: 'ri-tools-line',
      title: 'Obra Seca',
      description: 'Sin escombros ni suciedad excesiva'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Solución Profesional',
      description: 'Materiales de calidad y mano de obra experta'
    }
  ];

  // Stats data
  const stats = [
    { value: 500, suffix: '+', label: 'Proyectos realizados', icon: 'ri-building-2-line' },
    { value: 10000, suffix: '+', label: 'm² instalados', icon: 'ri-ruler-line' },
    { value: 15, suffix: '+', label: 'Años de experiencia', icon: 'ri-calendar-check-line' },
    { value: 100, suffix: '%', label: 'Clientes satisfechos', icon: 'ri-emotion-happy-line' }
  ];

  const testimonials = [
    {
      name: 'María García',
      location: 'Centre, Sabadell',
      rating: 5,
      text: 'Excelente trabajo en la redistribución de mi piso. Crearon dos habitaciones donde antes había una y el acabado quedó perfecto. Muy limpios y profesionales.',
      project: 'Tabiques divisorios',
      image: 'https://public.readdy.ai/ai/img_res/8a7ef04350fe5186c3797401a090578f.jpg'
    },
    {
      name: 'Carlos Martínez',
      location: 'Eixample, Sabadell',
      rating: 5,
      text: 'Instalaron el falso techo en mi oficina con iluminación integrada. El resultado es espectacular y cumplieron con los plazos acordados. Muy recomendables.',
      project: 'Falso techo',
      image: 'https://readdy.ai/api/search-image?query=professional%20headshot%20portrait%20of%20a%20spanish%20businessman%20in%20his%20forties%20friendly%20expression%20natural%20lighting%20neutral%20background%20smart%20casual%20attire%20high%20quality%20photography&width=100&height=100&seq=testimonial-carlos-002&orientation=squarish'
    },
    {
      name: 'Ana Rodríguez',
      location: 'Creu Alta, Sabadell',
      rating: 5,
      text: 'Necesitaba insonorizar el dormitorio por el ruido de la calle. Ahora duermo tranquila. El equipo fue muy atento y me explicaron todo el proceso.',
      project: 'Aislamiento acústico',
      image: 'https://readdy.ai/api/search-image?query=professional%20headshot%20portrait%20of%20a%20young%20spanish%20woman%20with%20confident%20smile%20natural%20lighting%20neutral%20background%20modern%20style%20high%20quality%20photography&width=100&height=100&seq=testimonial-ana-003&orientation=squarish'
    },
    {
      name: 'Jordi Puig',
      location: 'Can Feu, Sabadell',
      rating: 5,
      text: 'Reformaron el baño completo con pladur antihumedad. Llevan meses y ni rastro de humedades. Precio justo y trabajo impecable.',
      project: 'Pladur antihumedad',
      image: 'https://readdy.ai/api/search-image?query=professional%20headshot%20portrait%20of%20a%20spanish%20man%20in%20his%20fifties%20with%20glasses%20friendly%20demeanor%20natural%20lighting%20neutral%20background%20casual%20elegant%20style%20high%20quality%20photography&width=100&height=100&seq=testimonial-jordi-004&orientation=squarish'
    }
  ];

  const process = [
    {
      number: '01',
      title: 'Contacto',
      description: 'Llámanos o escríbenos por WhatsApp'
    },
    {
      number: '02',
      title: 'Visita y Medición',
      description: 'Evaluamos tu proyecto in situ'
    },
    {
      number: '03',
      title: 'Presupuesto',
      description: 'Propuesta detallada sin compromiso'
    },
    {
      number: '04',
      title: 'Ejecución',
      description: 'Instalación profesional y limpia'
    },
    {
      number: '05',
      title: 'Entrega',
      description: 'Acabados perfectos listos para usar'
    }
  ];

  const areas = [
    'Centre', 'Eixample', 'Creu Alta', 'Can Llong', 'Can Rull', 'Can Feu',
    'Gràcia', 'La Concòrdia', 'Torre-romeu', 'Ca n\'Oriac', 'La Planada del Pintor',
    'Terrassa', 'Barberà del Vallès', 'Badia', 'Sant Quirze', 'Castellar del Vallès',
    'Rubí', 'Cerdanyola del Vallès'
  ];

  const faqs = [
    {
      question: '¿Cuánto tarda la instalación de pladur en Sabadell?',
      answer: 'Depende del proyecto, pero un tabique estándar se instala en 1-2 días. Un techo completo puede llevar 2-4 días.'
    },
    {
      question: '¿El pladur aísla del ruido?',
      answer: 'Sí, con lana mineral o acústica en el interior conseguimos un excelente aislamiento acústico entre estancias.'
    },
    {
      question: '¿Se puede colgar peso en pladur?',
      answer: 'Sí, con tacos especiales para pladur o refuerzos internos se pueden colgar muebles, televisores y estanterías.'
    },
    {
      question: '¿Hacéis presupuestos sin compromiso?',
      answer: 'Por supuesto. Visitamos tu proyecto en Sabadell y alrededores y te damos un presupuesto detallado y gratuito.'
    }
  ];

  return (
    <div className="min-h-screen bg-dark-bg text-light-text">
      <Header />
      <WhatsAppButton />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <OptimizedImage
            src="https://readdy.ai/api/search-image?query=modern%20minimalist%20interior%20construction%20workspace%20with%20clean%20drywall%20installation%20professional%20lighting%20soft%20shadows%20neutral%20tones%20simple%20elegant%20background%20architectural%20details%20subtle%20texture%20high%20quality%20professional%20photography&width=1920&height=1080&seq=hero-pladurex-001&orientation=landscape"
            alt="Instalación profesional de pladur"
            className="w-full h-full object-cover object-top"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-dark-bg"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="w-full"
          >
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold mb-6 leading-tight">
              <span className="text-light-text">Instalación de Pladur</span>
              <br />
              <span className="text-primary-orange">en Sabadell</span>
            </h1>
            <p className="text-xl md:text-2xl text-light-muted mb-8 max-w-3xl mx-auto leading-relaxed">
              Especialistas en <strong className="text-light-text">tabiques, trasdosados y falsos techos</strong>. Acabados limpios, obra rápida y solución profesional para tu hogar u oficina.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="tel:+34629501941"
                className="flex items-center space-x-2 px-8 py-4 bg-primary-orange text-white rounded-lg font-semibold text-lg hover:shadow-glow-orange transition-all whitespace-nowrap"
              >
                <i className="ri-phone-line text-xl"></i>
                <span>629 501 941</span>
              </a>
              <a
                href="https://wa.me/34629501941"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 px-8 py-4 border-2 border-primary-orange text-primary-orange rounded-lg font-semibold text-lg hover:bg-primary-orange hover:text-white transition-all whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                <span>WhatsApp</span>
              </a>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <i className="ri-arrow-down-line text-3xl text-primary-orange"></i>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-dark-bg">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
              Nuestros <span className="text-primary-orange">servicios</span>
            </h2>
            <p className="text-lg text-light-muted max-w-2xl mx-auto">
              Soluciones completas de <a href="https://terrassa.pladures.es/" target="_blank" rel="noopener noreferrer" className="text-primary-orange hover:underline">pladur para todo tipo de proyectos en Sabadell</a> y alrededores
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-dark-card p-8 rounded-xl border border-dark-lighter hover:border-primary-orange/50 transition-all cursor-pointer"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-primary-orange/10 rounded-lg mb-6">
                  <i className={`${service.icon} text-3xl text-primary-orange`}></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                <p className="text-light-muted mb-4 leading-relaxed">{service.description}</p>
                <Link
                  to={service.link}
                  className="inline-flex items-center text-primary-orange hover:text-primary-orange-light transition-colors font-medium whitespace-nowrap"
                >
                  Más información
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-dark-card">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
              ¿Por qué elegir <span className="text-primary-orange">pladur</span>?
            </h2>
            <p className="text-lg text-light-muted max-w-2xl mx-auto">
              Ventajas de trabajar con pladur en tu proyecto de reforma o construcción
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-primary-orange/10 rounded-full mx-auto mb-4">
                  <i className={`${benefit.icon} text-3xl text-primary-orange`}></i>
                </div>
                <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                <p className="text-light-muted text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Animated Stats Section */}
      <section className="py-20 bg-dark-bg relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-orange rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary-orange rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
              Cifras que <span className="text-primary-orange">avalan</span> nuestro trabajo
            </h2>
            <p className="text-lg text-light-muted max-w-2xl mx-auto">
              Resultados que demuestran nuestra experiencia en instalación de pladur en Sabadell
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {stats.map((stat, index) => (
              <AnimatedStatCard key={index} stat={stat} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Certifications & Guarantees Section */}
      <section className="py-20 bg-dark-card relative overflow-hidden">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
              Certificaciones y <span className="text-primary-orange">garantías</span>
            </h2>
            <p className="text-lg text-light-muted max-w-2xl mx-auto">
              Trabajamos con los más altos estándares de calidad y ofrecemos garantías reales en todos nuestros proyectos. Contamos con más de 15 años de <a href="https://pladures.es/barcelona/sabadell/" target="_blank" rel="noopener noreferrer" className="text-primary-orange hover:underline">experiencia en instalación de pladur en Sabadell</a>
            </p>
          </motion.div>

          {/* Certifications */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Professional Certifications */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-dark-bg p-8 rounded-2xl border border-dark-lighter hover:border-primary-orange/30 transition-all"
            >
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-primary-orange/10 rounded-xl">
                  <i className="ri-award-line text-3xl text-primary-orange"></i>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-light-text">Certificaciones profesionales</h3>
                  <p className="text-sm text-light-muted">Acreditaciones oficiales</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3 p-4 bg-dark-card rounded-xl border border-dark-lighter">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-orange/10 rounded-lg flex-shrink-0">
                    <i className="ri-verified-badge-line text-xl text-primary-orange"></i>
                  </div>
                  <div>
                    <h4 className="font-medium text-light-text">Instalador certificado Placo®</h4>
                    <p className="text-sm text-light-muted">Formación oficial del fabricante líder en sistemas de pladur</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3 p-4 bg-dark-card rounded-xl border border-dark-lighter">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-orange/10 rounded-lg flex-shrink-0">
                    <i className="ri-shield-star-line text-xl text-primary-orange"></i>
                  </div>
                  <div>
                    <h4 className="font-medium text-light-text">Seguro de responsabilidad civil</h4>
                    <p className="text-sm text-light-muted">Cobertura completa para tu tranquilidad durante la obra</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3 p-4 bg-dark-card rounded-xl border border-dark-lighter">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-orange/10 rounded-lg flex-shrink-0">
                    <i className="ri-file-list-3-line text-xl text-primary-orange"></i>
                  </div>
                  <div>
                    <h4 className="font-medium text-light-text">Alta en el IAE</h4>
                    <p className="text-sm text-light-muted">Empresa legalmente constituida con factura oficial</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Guarantees */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-dark-bg p-8 rounded-2xl border border-dark-lighter hover:border-primary-orange/30 transition-all"
            >
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-primary-orange/10 rounded-xl">
                  <i className="ri-shield-check-line text-3xl text-primary-orange"></i>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-light-text">Nuestras garantías</h3>
                  <p className="text-sm text-light-muted">Compromiso por escrito</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3 p-4 bg-dark-card rounded-xl border border-primary-orange/30">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-orange rounded-lg flex-shrink-0">
                    <i className="ri-time-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="font-medium text-light-text">Garantía de 5 años</h4>
                    <p className="text-sm text-light-muted">En todos los trabajos de instalación de pladur</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3 p-4 bg-dark-card rounded-xl border border-dark-lighter">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-orange/10 rounded-lg flex-shrink-0">
                    <i className="ri-money-euro-circle-line text-xl text-primary-orange"></i>
                  </div>
                  <div>
                    <h4 className="font-medium text-light-text">Presupuesto cerrado</h4>
                    <p className="text-sm text-light-muted">Sin sorpresas ni costes adicionales ocultos</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3 p-4 bg-dark-card rounded-xl border border-dark-lighter">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-orange/10 rounded-lg flex-shrink-0">
                    <i className="ri-calendar-check-line text-xl text-primary-orange"></i>
                  </div>
                  <div>
                    <h4 className="font-medium text-light-text">Cumplimiento de plazos</h4>
                    <p className="text-sm text-light-muted">Entregamos en la fecha acordada o compensamos</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Trust Badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-primary-orange/5 via-primary-orange/10 to-primary-orange/5 p-8 rounded-2xl border border-primary-orange/20"
          >
            <div className="text-center mb-8">
              <h3 className="text-2xl font-semibold text-light-text mb-2">Sellos de confianza</h3>
              <p className="text-light-muted">Materiales de primera calidad de marcas líderes</p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-dark-bg p-6 rounded-xl border border-dark-lighter hover:border-primary-orange/30 transition-all text-center"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-white rounded-xl mx-auto mb-3 overflow-hidden">
                  <span className="text-xl font-bold text-gray-800 px-2 truncate">Placo®</span>
                </div>
                <p className="text-sm text-light-muted">Placas oficiales</p>
              </motion.div>
              
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-dark-bg p-6 rounded-xl border border-dark-lighter hover:border-primary-orange/30 transition-all text-center"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-white rounded-xl mx-auto mb-3 overflow-hidden">
                  <span className="text-xl font-bold text-gray-800 px-2 truncate">Knauf</span>
                </div>
                <p className="text-sm text-light-muted">Sistemas certificados</p>
              </motion.div>
              
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-dark-bg p-6 rounded-xl border border-dark-lighter hover:border-primary-orange/30 transition-all text-center"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-white rounded-xl mx-auto mb-3 overflow-hidden">
                  <span className="text-base font-bold text-gray-800 px-2 truncate">ISOVER</span>
                </div>
                <p className="text-sm text-light-muted">Aislamiento térmico</p>
              </motion.div>
              
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-dark-bg p-6 rounded-xl border border-dark-lighter hover:border-primary-orange/30 transition-all text-center"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-white rounded-xl mx-auto mb-3 overflow-hidden">
                  <span className="text-sm font-bold text-gray-800 px-1 truncate">Rockwool</span>
                </div>
                <p className="text-sm text-light-muted">Lana de roca</p>
              </motion.div>
            </div>
          </motion.div>

          {/* Additional Trust Elements */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="flex items-center space-x-4 p-5 bg-dark-bg rounded-xl border border-dark-lighter"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-green-500/10 rounded-full flex-shrink-0">
                <i className="ri-leaf-line text-2xl text-green-500"></i>
              </div>
              <div>
                <h4 className="font-medium text-light-text">Materiales ecológicos</h4>
                <p className="text-xs text-light-muted">Productos sostenibles y reciclables</p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="flex items-center space-x-4 p-5 bg-dark-bg rounded-xl border border-dark-lighter"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-primary-orange/10 rounded-full flex-shrink-0">
                <i className="ri-fire-line text-2xl text-primary-orange"></i>
              </div>
              <div>
                <h4 className="font-medium text-light-text">Resistencia al fuego</h4>
                <p className="text-xs text-light-muted">Placas con certificación RF</p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="flex items-center space-x-4 p-5 bg-dark-bg rounded-xl border border-dark-lighter"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-sky-500/10 rounded-full flex-shrink-0">
                <i className="ri-customer-service-2-line text-2xl text-sky-500"></i>
              </div>
              <div>
                <h4 className="font-medium text-light-text">Atención postventa</h4>
                <p className="text-xs text-light-muted">Soporte continuo tras la obra</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-dark-bg">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
              Lo que dicen nuestros <span className="text-primary-orange">clientes</span>
            </h2>
            <p className="text-lg text-light-muted max-w-2xl mx-auto">
              La satisfacción de nuestros clientes en Sabadell es nuestra mejor carta de presentación
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-dark-card p-6 rounded-xl border border-dark-lighter hover:border-primary-orange/30 transition-all"
              >
                <div className="flex items-start space-x-4 mb-4">
                  <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 border-2 border-primary-orange/30">
                    <OptimizedImage
                      src={testimonial.image}
                      alt={testimonial.name}
                      loading="lazy"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-light-text">{testimonial.name}</h3>
                    <p className="text-sm text-light-muted">{testimonial.location}</p>
                    <div className="flex items-center space-x-1 mt-1">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-primary-orange text-sm"></i>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-light-muted leading-relaxed mb-4">"{testimonial.text}"</p>
                <div className="flex items-center space-x-2">
                  <span className="px-3 py-1 bg-primary-orange/10 text-primary-orange text-xs rounded-full">
                    {testimonial.project}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <p className="text-light-muted mb-4">¿Quieres ser nuestro próximo cliente satisfecho?</p>
            <a
              href="tel:+34629501941"
              className="inline-flex items-center px-6 py-3 bg-primary-orange text-white rounded-lg font-semibold hover:shadow-glow-orange transition-all whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              Solicita presupuesto gratis
            </a>
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-dark-card">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
              Cómo <span className="text-primary-orange">trabajamos</span>
            </h2>
            <p className="text-lg text-light-muted max-w-2xl mx-auto">
              Proceso simple y transparente en 5 pasos
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {process.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <div className="bg-dark-card p-6 rounded-xl border border-dark-lighter h-full">
                  <div className="text-5xl font-display font-bold text-primary-orange/20 mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
                  <p className="text-light-muted text-sm">{step.description}</p>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <i className="ri-arrow-right-line text-2xl text-primary-orange/50"></i>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas with Interactive Map */}
      <section className="py-20 bg-dark-card">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
              Zonas de <span className="text-primary-orange">servicio</span>
            </h2>
            <p className="text-lg text-light-muted max-w-2xl mx-auto">
              Realizamos instalaciones de pladur en Sabadell y toda la comarca del Vallès
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            {/* Interactive Map */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden border-2 border-dark-lighter hover:border-primary-orange/50 transition-all duration-300 shadow-xl">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d95548.55774828!2d2.0234!3d41.5463!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12a4960d4c2b5a8d%3A0x4c0b0f0f0f0f0f0f!2sSabadell%2C%20Barcelona%2C%20Spain!5e0!3m2!1ses!2ses!4v1699999999999!5m2!1ses!2ses&maptype=roadmap&z=11"
                  width="100%"
                  height="450"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Mapa de zonas de servicio PLADUREX en Sabadell"
                  className="w-full"
                ></iframe>
              </div>
              
              {/* Map overlay badge */}
              <div className="absolute top-4 left-4 bg-dark-bg/90 backdrop-blur-sm px-4 py-2 rounded-lg border border-primary-orange/30">
                <div className="flex items-center space-x-2">
                  <i className="ri-map-pin-line text-primary-orange"></i>
                  <span className="text-sm font-medium text-light-text">Área de cobertura</span>
                </div>
              </div>
            </motion.div>

            {/* Zones List */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              {/* Sabadell - Main */}
              <div className="bg-dark-bg p-6 rounded-xl border border-primary-orange/30">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-orange rounded-lg">
                    <i className="ri-building-4-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-light-text">Sabadell</h3>
                    <p className="text-xs text-primary-orange">Zona principal</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {['Centre', 'Eixample', 'Creu Alta', 'Can Llong', 'Can Rull', 'Can Feu', 'Gràcia', 'La Concòrdia', 'Torre-romeu', 'Ca n\'Oriac', 'La Planada del Pintor'].map((barrio, index) => (
                    <motion.span
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.03 }}
                      className="px-3 py-1.5 bg-dark-card border border-dark-lighter rounded-full text-xs text-light-muted hover:border-primary-orange hover:text-primary-orange transition-colors cursor-default whitespace-nowrap"
                    >
                      {barrio}
                    </motion.span>
                  ))}
                </div>
              </div>

              {/* Vallès Occidental */}
              <div className="bg-dark-bg p-6 rounded-xl border border-dark-lighter hover:border-primary-orange/30 transition-colors">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-orange/20 rounded-lg">
                    <i className="ri-map-2-line text-xl text-primary-orange"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-light-text">Vallès Occidental</h3>
                    <p className="text-xs text-light-muted">Municipios cercanos</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {['Terrassa', 'Barberà del Vallès', 'Badia del Vallès', 'Sant Quirze del Vallès', 'Castellar del Vallès', 'Rubí', 'Cerdanyola del Vallès'].map((municipio, index) => (
                    <motion.span
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.05 }}
                      className="px-3 py-1.5 bg-dark-card border border-dark-lighter rounded-full text-xs text-light-muted hover:border-primary-orange hover:text-primary-orange transition-colors cursor-default whitespace-nowrap"
                    >
                      {municipio}
                    </motion.span>
                  ))}
                </div>
              </div>

              {/* Contact CTA */}
              <div className="bg-gradient-to-r from-primary-orange/10 to-primary-orange/5 p-6 rounded-xl border border-primary-orange/20">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-primary-orange/20 rounded-full flex-shrink-0">
                    <i className="ri-question-line text-2xl text-primary-orange"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-light-text mb-1">¿No encuentras tu zona?</h3>
                    <p className="text-sm text-light-muted mb-3">Consúltanos sin compromiso. Podemos desplazarnos a otras zonas del área metropolitana de Barcelona.</p>
                    <a
                      href="https://wa.me/34629501941?text=Hola,%20quiero%20saber%20si%20trabajáis%20en%20mi%20zona"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm text-primary-orange hover:text-primary-orange-light transition-colors font-medium whitespace-nowrap"
                    >
                      <i className="ri-whatsapp-line mr-2"></i>
                      Consultar disponibilidad
                      <i className="ri-arrow-right-line ml-1"></i>
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mini FAQ */}
      <section className="py-20 bg-dark-bg">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
              Preguntas <span className="text-primary-orange">frecuentes</span>
            </h2>
            <p className="text-lg text-light-muted max-w-2xl mx-auto">
              Resolvemos tus dudas sobre la instalación de pladur en Sabadell
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-dark-card p-6 rounded-xl border border-dark-lighter"
              >
                <h3 className="text-lg font-semibold mb-3 text-primary-orange">{faq.question}</h3>
                <p className="text-light-muted leading-relaxed">{faq.answer}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/faq"
              className="inline-flex items-center px-6 py-3 bg-primary-orange text-white rounded-lg font-semibold hover:shadow-glow-orange transition-all whitespace-nowrap"
            >
              Ver todas las preguntas
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-orange to-primary-orange-light">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 text-white">
              ¿Listo para tu proyecto de pladur?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Contacta con nosotros y recibe un presupuesto personalizado sin compromiso para tu instalación de pladur en Sabadell
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="tel:+34629501941"
                className="flex items-center space-x-2 px-8 py-4 bg-white text-primary-orange rounded-lg font-semibold text-lg hover:shadow-xl transition-all whitespace-nowrap"
              >
                <i className="ri-phone-line text-xl"></i>
                <span>Llamar ahora</span>
              </a>
              <Link
                to="/contacto"
                className="flex items-center space-x-2 px-8 py-4 border-2 border-white text-white rounded-lg font-semibold text-lg hover:bg-white hover:text-primary-orange transition-all whitespace-nowrap"
              >
                <i className="ri-mail-line text-xl"></i>
                <span>Formulario de contacto</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <CookieBanner />
      <ScrollToTopButton />
    </div>
  );
};

// Animated Stat Card Component
const AnimatedStatCard = ({ stat, index }: { stat: { value: number; suffix: string; label: string; icon: string }; index: number }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          animateCount();
        }
      },
      { threshold: 0.2 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  const animateCount = () => {
    const duration = 2500;
    const startTime = performance.now();
    const startValue = 0;
    const endValue = stat.value;

    const easeOutExpo = (t: number): number => {
      return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
    };

    const updateCount = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutExpo(progress);
      const currentValue = Math.floor(startValue + (endValue - startValue) * easedProgress);
      
      setCount(currentValue);

      if (progress < 1) {
        requestAnimationFrame(updateCount);
      } else {
        setCount(endValue);
      }
    };

    requestAnimationFrame(updateCount);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000) {
      return num.toLocaleString('es-ES');
    }
    return num.toString();
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.15, duration: 0.6, ease: "easeOut" }}
      className="relative group"
    >
      <div className="bg-dark-card p-6 lg:p-8 rounded-2xl border border-dark-lighter hover:border-primary-orange/50 transition-all duration-300 text-center h-full">
        {/* Icon */}
        <div className="w-14 h-14 flex items-center justify-center bg-primary-orange/10 rounded-xl mx-auto mb-4 group-hover:bg-primary-orange/20 transition-colors">
          <i className={`${stat.icon} text-3xl text-primary-orange`}></i>
        </div>
        
        {/* Counter */}
        <motion.div 
          className="text-4xl lg:text-5xl font-display font-bold text-primary-orange mb-2"
          initial={{ scale: 1 }}
          animate={hasAnimated ? { scale: [1, 1.05, 1] } : {}}
          transition={{ duration: 0.3, delay: 2.3 }}
        >
          <span>{formatNumber(count)}</span>
          <span>{stat.suffix}</span>
        </motion.div>
        
        {/* Label */}
        <p className="text-light-muted text-sm lg:text-base">{stat.label}</p>
        
        {/* Glow effect on hover */}
        <div className="absolute inset-0 rounded-2xl bg-primary-orange/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
      </div>
    </motion.div>
  );
};

export default HomePage;
